import { useRef, useState } from "react";
import { Trash2, Save, UploadCloud } from "lucide-react";
import { toast } from "sonner";

const MAX_DIM = 256; // cap so huge images stay a reasonable grid

/**
 * Pattern maker (Experimental). Users design a pattern in any external image
 * editor and IMPORT it here — white areas become the base material, black
 * areas become the pattern. The image keeps (almost) its own size: it is
 * sampled at native resolution, only downscaled if a side exceeds MAX_DIM.
 */
export const PatternMaker = ({ patterns = [], onSave, onDelete }) => {
  const inputRef = useRef(null);
  const [preview, setPreview] = useState(null); // { width, height, cells }
  const [name, setName] = useState("");

  const sampleImage = (img) => {
    let w = img.naturalWidth || img.width;
    let h = img.naturalHeight || img.height;
    const scale = Math.min(1, MAX_DIM / Math.max(w, h));
    w = Math.max(1, Math.round(w * scale));
    h = Math.max(1, Math.round(h * scale));
    const c = document.createElement("canvas");
    c.width = w;
    c.height = h;
    const ctx = c.getContext("2d", { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, w, h);
    const data = ctx.getImageData(0, 0, w, h).data;
    const cells = new Array(w * h);
    for (let i = 0; i < w * h; i++) {
      const k = i * 4;
      const lum = 0.2126 * data[k] + 0.7152 * data[k + 1] + 0.0722 * data[k + 2];
      // Black -> pattern (ON). White -> base (OFF).
      cells[i] = data[k + 3] > 10 && lum < 128;
    }
    return { width: w, height: h, cells };
  };

  const handleFile = (file) => {
    if (!file || !file.type.startsWith("image/")) {
      toast.error("Please import an image (PNG / JPG / WEBP).");
      return;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const sampled = sampleImage(img);
      URL.revokeObjectURL(url);
      if (!sampled.cells.some(Boolean)) {
        toast.error("This image is all white — no pattern detected.");
        return;
      }
      setPreview(sampled);
      if (!name.trim()) setName(file.name.replace(/\.[^.]+$/, ""));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      toast.error("Could not read that image.");
    };
    img.src = url;
  };

  const save = () => {
    if (!preview) {
      toast.error("Import an image first.");
      return;
    }
    const trimmed = name.trim() || `Pattern ${patterns.length + 1}`;
    onSave({
      id: `pat_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      name: trimmed,
      width: preview.width,
      height: preview.height,
      cells: preview.cells.slice(),
    });
    setPreview(null);
    setName("");
    toast.success(`Saved pattern "${trimmed}"`);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <span className="text-sm text-zinc-100 font-medium">Pattern maker</span>
        <span
          data-testid="pattern-maker-experimental"
          className="rounded-full border border-amber-400/30 bg-amber-500/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-amber-300"
        >
          Experimental
        </span>
      </div>

      <p className="text-xs text-zinc-500 -mt-1 leading-relaxed">
        Make your pattern in any image editor, then import it here.{" "}
        <span className="text-zinc-300">White</span> areas become the base
        material and <span className="text-zinc-300">black</span> areas become
        the pattern. The image can be almost any size &mdash; it&apos;s used at its
        own resolution (downscaled only if a side is over {MAX_DIM}px).
      </p>

      <div className="flex items-center gap-3">
        <button
          data-testid="pattern-import-btn"
          onClick={() => inputRef.current?.click()}
          className="flex items-center gap-2 rounded-lg border border-dashed border-zinc-700 bg-[#0d0e12] px-3 py-2.5 text-sm text-zinc-300 hover:text-white hover:border-rose-400/60 transition flex-1"
        >
          <UploadCloud className="w-4 h-4" />
          Import pattern image
        </button>
        {preview && (
          <div className="flex flex-col items-center gap-1 flex-shrink-0">
            <PatternThumb pattern={preview} testId="pattern-preview" box={56} />
            <span className="text-[10px] mono text-zinc-500">
              {preview.width}×{preview.height}
            </span>
          </div>
        )}
      </div>
      <input
        ref={inputRef}
        data-testid="pattern-import-input"
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => handleFile(e.target.files?.[0])}
      />

      <div className="flex items-center gap-2">
        <input
          data-testid="pattern-name-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Pattern name"
          className="flex-1 h-9 rounded-md border border-zinc-800 bg-[#131418] px-3 text-sm text-zinc-100 placeholder:text-zinc-600 outline-none focus:border-rose-500/60 transition"
        />
        <button
          data-testid="pattern-save"
          onClick={save}
          disabled={!preview}
          className="flex items-center gap-1.5 brand-gradient text-white text-sm font-semibold rounded-md px-3 h-9 hover:opacity-95 transition disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Save className="w-4 h-4" /> Save
        </button>
      </div>

      {patterns.length > 0 && (
        <div className="space-y-1.5 pt-1">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
            Saved patterns
          </div>
          <div className="space-y-1.5 max-h-40 overflow-y-auto scroll-thin pr-1">
            {patterns.map((p) => (
              <div
                key={p.id}
                data-testid={`saved-pattern-${p.id}`}
                className="flex items-center gap-2 rounded-md border border-zinc-800 bg-[#131418] p-1.5"
              >
                <PatternThumb pattern={p} box={40} />
                <span className="text-sm text-zinc-100 flex-1 truncate">{p.name}</span>
                <span className="text-[10px] mono text-zinc-500">
                  {(p.width ?? p.size)}×{(p.height ?? p.size)}
                </span>
                <button
                  data-testid={`delete-pattern-${p.id}`}
                  onClick={() => onDelete(p.id)}
                  className="p-1 text-zinc-500 hover:text-rose-400 transition"
                  aria-label="Delete pattern"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Renders the pattern preview as an <img> so any size scales cleanly.
const PatternThumb = ({ pattern, testId, box = 40 }) => {
  const w = pattern.width ?? pattern.size;
  const h = pattern.height ?? pattern.size;
  const src = cellsToDataUrl(pattern.cells, w, h);
  return (
    <img
      data-testid={testId}
      src={src}
      alt="pattern"
      width={box}
      height={box}
      className="rounded border border-zinc-700 flex-shrink-0"
      style={{ imageRendering: "pixelated", objectFit: "contain", background: "#0d0e12" }}
    />
  );
};

function cellsToDataUrl(cells, w, h) {
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  const ctx = c.getContext("2d");
  const img = ctx.createImageData(w, h);
  const d = img.data;
  for (let i = 0; i < w * h; i++) {
    const k = i * 4;
    if (cells[i]) {
      d[k] = 244;
      d[k + 1] = 63;
      d[k + 2] = 94; // rose accent = pattern
    } else {
      d[k] = 13;
      d[k + 1] = 14;
      d[k + 2] = 18; // base
    }
    d[k + 3] = 255;
  }
  ctx.putImageData(img, 0, 0);
  return c.toDataURL();
}
